import React, { useState, useEffect, useMemo } from "react";
import debounce from "lodash.debounce";
import { Accordion } from "../components/setting-accordion";
import { SaveButton } from "../components/save-button";
import SettingIcon from "../../../../../../../images/settings-icons/setting-protein.png";
import HelpTip from "../../../../../../../components/help-tip";
import IconButton from "@material-ui/core/IconButton";
import { ReactComponent as LockIcon } from "../../../../../../../images/settings-icons/lock-icon.svg";
import { ReactComponent as LockOpenIcon } from "../../../../../../../images/settings-icons/unlock-icon.svg";
import RefreshIcon from "@material-ui/icons/Refresh";
import MacroItemComponent from "./macro-item";
import { getDaysSinceWeighIn } from "../../../util";
import { UserCheckinWarning } from "../components/user-check-in-warning";
import CheckinFormPortal from "../components/check-in-2-4";
import VacationModeModal from "../vacation-mode-modal";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import HighLowIcon from "../../../../../../../images/settings-icons/setting-tour.png";
import { saveHighLow2 } from "../../../../../../../api";
import { toDateString } from "../../../util";
import {
  Container,
  Section,
  TitleAndInput,
  TitleText,
  CalorieGoalContainer,
  Input,
  OriginalGoalText,
  RestoreButton,
  ViewToggle,
  ToggleButton,
  MacroGrid,
  ErrorBox,
  ErrorIcon,
  ButtonRow,
} from "./styled-components";
import {
  calculatePercentages,
  isValidValue,
  getFatPermyriad,
  recalculateMacrosForCalorieGoalChange,
  recalculateMacrosForMacroChange,
  validateCalorieFloor,
  validateMacros,
  validateHighLowPlan,
} from "./calculations";
import { getRecommendedMacros, modifyUser } from "../../../../../../../api";

/*
When editing macros, there are general rules to follow so the macros are being distributed properly when calories are locked.

When changing calories, keep the ratio of protein/carbs/fat percentages
+ Protein --> Take calories from carbs/fat so that fat percentage (of total calories) stays the same (when carbs become 0, then only take from fat afterwards)
- Protein --> Add calories to carbs/fat so that fat percentage (of total calories) stays the same (until an upper limit is hit)
+ Carbs --> Take calories from fat until fat floor is reached, then take from protein until floor is reached
- Carbs --> Add calories to fat until carbs become 0 (shouldn't ever need to add to protein since I don't think fat has a limit)
+ Fat --> Take calories from carbs until carbs become 0, then take from protein until floor is reached
- Fat --> Add calories to carbs until fat floor is reached
*/

const UpdateCaloriesMacros = (props) => {
  const MACRO_RECALC_DEBOUNCE_MS = 450;
  const debouncedApplyMacroInputChange = useMemo(
    () =>
      debounce((callback) => {
        callback();
      }, MACRO_RECALC_DEBOUNCE_MS),
    []
  );
  const debouncedApplyPercentageInputChange = useMemo(
    () =>
      debounce((callback) => {
        callback();
      }, MACRO_RECALC_DEBOUNCE_MS),
    []
  );

  // Macro values and lock states grouped together
  const [macroState, setMacroState] = useState({
    proteinGrams: null,
    carbsGrams: null,
    fatGrams: null,
    calorieGoal: null,
    calorieFloor: null,
    caloriesGoalLocked: true,
    calorieFloorLocked: false,
    proteinLocked: false,
    carbsLocked: false,
    fatLocked: false,
  });

  // UI state
  const [uiState, setUiState] = useState({
    isLoading: false,
    show: false,
    viewMode: "grams", // "grams" or "percent"
  });

  // Modal states
  const [modalState, setModalState] = useState({
    showUserCheckinWarning: false,
    showCheckinFormPortal: false,
    showVacationModal: false,
    showHighLowResetModal: false,
  });

  // Validation and data
  const [dataState, setDataState] = useState({
    calcError: [],
    recommendedMacros: null,
    lastValidMacros: {
      protein: null,
      carbs: null,
      fat: null,
    },
  });

  // Percentage inputs
  const [percentageInputs, setPercentageInputs] = useState({
    proteinPercentageInput: null,
    carbsPercentageInput: null,
    fatPercentageInput: null,
  });
  const [macroInputDrafts, setMacroInputDrafts] = useState({
    protein: null,
    carbs: null,
    fat: null,
  });
  const [editingPercentageMacro, setEditingPercentageMacro] = useState(null);

  // Destructure for easier access
  const {
    proteinGrams,
    carbsGrams,
    fatGrams,
    calorieGoal,
    calorieFloor,
    caloriesGoalLocked,
    calorieFloorLocked,
    proteinLocked,
    carbsLocked,
    fatLocked,
  } = macroState;

  const { isLoading, show, viewMode } = uiState;
  const { showUserCheckinWarning, showCheckinFormPortal, showVacationModal, showHighLowResetModal } = modalState;
  const { calcError, recommendedMacros, lastValidMacros } = dataState;
  const { proteinPercentageInput, carbsPercentageInput, fatPercentageInput } = percentageInputs;

  // Helper functions to update each state group
  const updateMacroState = (updates) => {
    setMacroState((prev) => ({ ...prev, ...updates }));
  };

  const updateUiState = (updates) => {
    setUiState((prev) => ({ ...prev, ...updates }));
  };

  const updateModalState = (updates) => {
    setModalState((prev) => ({ ...prev, ...updates }));
  };

  const updateDataState = (updates) => {
    setDataState((prev) => ({ ...prev, ...updates }));
  };

  const updatePercentageInputs = (updates) => {
    setPercentageInputs((prev) => ({ ...prev, ...updates }));
  };

  const percentages = calculatePercentages(
    proteinGrams,
    carbsGrams,
    fatGrams,
    calorieGoal
  );

  useEffect(() => {
    if (props.portalId && props.account?.user?.portal_user_info?.id) {
      getRecommendedMacros(
        props.portalId,
        props.account.user.portal_user_info.id
      )
        .then((response) => {
          if (response.data) {
            updateDataState({
              recommendedMacros: {
                protein: response.data.protein,
                carbs: response.data.carbs,
                fat: response.data.fat,
                calories: response.data.calories,
                calorie_floor: response.data.calorie_floor,
              },
            });
            if (!calorieGoal) {
              updateMacroState({ calorieGoal: response.data.calories });
            }
            if (!calorieFloor) {
              updateMacroState({ calorieFloor: response.data.calorie_floor });
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching recommended macros:", error);
        });
    }
  }, [show, props.portalId, props.account?.user?.portal_user_info?.id]);

  // Automatically lock calories when in percent view
  useEffect(() => {
    if (viewMode === "percent") {
      updateMacroState({ caloriesGoalLocked: true });
    }
  }, [viewMode]);

  useEffect(() => {
    return () => {
      debouncedApplyMacroInputChange.cancel();
      debouncedApplyPercentageInputChange.cancel();
    };
  }, [debouncedApplyMacroInputChange, debouncedApplyPercentageInputChange]);

  // Load calorie goal, floor, and macros from props
  useEffect(() => {
    if (props.account?.user) {
      const user = props.account.user;
      const newCalorieGoal =
        user.custom_calories ??
        user.calories ??
        recommendedMacros?.calories ??
        0;

      const hasLastMacros =
        user.last_protein !== null &&
        user.last_protein !== undefined &&
        user.last_carbs !== null &&
        user.last_carbs !== undefined &&
        user.last_fat !== null &&
        user.last_fat !== undefined;

      let protein = null;
      let carbs = null;
      let fat = null;

      if (hasLastMacros) {
        protein = user.last_protein;
        carbs = user.last_carbs;
        fat = user.last_fat;
      } else {
        const baseProtein = recommendedMacros?.protein ?? user.protein ?? 0;
        protein = baseProtein + (user.protein_adjustment || 0);

        if (
          newCalorieGoal > 0 &&
          user.fat_permyriad !== null &&
          user.fat_permyriad !== undefined
        ) {
          fat = (newCalorieGoal * user.fat_permyriad) / 90000;
        }

        if (newCalorieGoal > 0 && fat !== null) {
          carbs = (newCalorieGoal - protein * 4 - fat * 9) / 4;
          carbs = Math.max(0, carbs);
        }
      }

      updateMacroState({
        calorieGoal: newCalorieGoal,
        calorieFloor: user.custom_calorie_floor || recommendedMacros?.calorie_floor,
        proteinGrams: protein,
        fatGrams: fat,
        carbsGrams: carbs,
      });
      if (protein !== null && carbs !== null && fat !== null) {
        updateDataState({
          lastValidMacros: { protein, carbs, fat }
        });
      }
    }
  }, [
    props.account?.user?.protein,
    props.account?.user?.protein_adjustment,
    props.account?.user?.custom_calories,
    props.account?.user?.calories,
    props.account?.user?.fat_permyriad,
    props.account?.user?.last_protein,
    props.account?.user?.last_carbs,
    props.account?.user?.last_fat,
    props.account?.user?.custom_calorie_floor,
    recommendedMacros,
  ]);

  // Get current state object for calculation functions
  const getCurrentState = () => ({
    ...macroState,
    ...dataState,
    protein: proteinGrams,
    carbs: carbsGrams,
    fat: fatGrams,
    weight: props.account.user.weight,
    bodyFat: props.account.user.edited_body_fat ?? props.account.user.body_fat ?? 0,
  });


  const resetHighLowPlan = async (newCarbs, newFat) => {
    // Get current high/low plan from account (or initialize with defaults)
    const currentHighLowPlan = props.account?.user?.high_low_plan;
    const highLowPlan = currentHighLowPlan && Array.isArray(currentHighLowPlan) && currentHighLowPlan.length === 7
      ? [...currentHighLowPlan] // Copy existing plan
      : Array(7).fill(null).map(() => ({ carbs: newCarbs, fat: newFat })); // Initialize with new macros
    
    // Get last weigh-in date and convert to Date object (same format as update-high-low-2025)
    const lastWeighInDateString = props.account?.user?.lastWeighInDate;
    if (!lastWeighInDateString) {
      // If no last weigh-in date, can't reset properly - just set all days
      const allDaysPlan = Array(7).fill(null).map(() => ({ carbs: newCarbs, fat: newFat }));
      const localToday = toDateString(new Date());
      return saveHighLow2(
        props.account.user.id,
        props.portalId,
        toDateString(new Date()),
        allDaysPlan,
        props.account.user.high_low_every_week || 0,
        localToday
      );
    }
    
    const lastWeighInDateTemp = new Date(lastWeighInDateString);
    const lastWeighInDate = new Date(
      lastWeighInDateTemp.getUTCFullYear(),
      lastWeighInDateTemp.getUTCMonth(),
      lastWeighInDateTemp.getUTCDate()
    );
    
    // Get today's date (at midnight for comparison)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Reset only non-past days (same logic as handleReset in update-high-low-2025)
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(lastWeighInDate);
      dayDate.setDate(dayDate.getDate() + i);
      dayDate.setHours(0, 0, 0, 0);
      const isPast = dayDate < today;
      
      // Only reset future days to new macros, keep past days as they are
      if (!isPast) {
        highLowPlan[i] = {
          carbs: newCarbs,
          fat: newFat,
        };
      }
    }
    
    // Format local_today as date string (YYYY-MM-DD)
    const localToday = toDateString(new Date());

    // Save the reset high/low plan (keep existing high_low_every_week setting)
    return saveHighLow2(
      props.account.user.id,
      props.portalId,
      lastWeighInDateString,
      highLowPlan,
      props.account.user.high_low_every_week || 0, // Keep existing setting
      localToday
    );
  };

  const handleSave = async () => {
    // Check for errors (high/low errors are always excluded from blocking save)
    const validation = validateAndSetErrors(proteinGrams, carbsGrams, fatGrams, calorieGoal);
    
    // Block save if there are non-high/low errors
    if (!validation.valid) {
      return;
    }
    
    updateUiState({ isLoading: true });

    // Check if high/low plan will be invalidated (from validation result)
    const willInvalidateHighLow = validation.highLowInvalid;

    const currentProteinAdjustment = props.account?.user?.protein_adjustment || 0;
    const currentSavedProtein = isValidValue(props.account?.user?.last_protein)
      ? props.account.user.last_protein
      : (recommendedMacros?.protein || 0) + currentProteinAdjustment;
    const baseProtein = currentSavedProtein - currentProteinAdjustment;
    const proteinAdjustment = proteinGrams - baseProtein;

    const fatPermyriad = getFatPermyriad(fatGrams, calorieGoal);

    let mods = {};
    if (calorieGoal !== props.account.user.custom_calories) {
      mods["custom_calories"] = Number(calorieGoal);
    }
    if (calorieFloor !== props.account.user.custom_calorie_floor) {
      mods["custom_calorie_floor"] = Number(calorieFloor);
    }
    mods["protein_adjustment"] = proteinAdjustment;
    mods["fat_permyriad"] = fatPermyriad;

    modifyUser(props.userId, props.portalId, mods)
      .then(async (res) => {
        // Always reload account first to get updated macro values
        if (props.loadPortalAccount) {
          await props.loadPortalAccount();
        }
        
        updateUiState({ isLoading: false });
        
        // If high/low plan was invalidated, reset it and show modal
        if (willInvalidateHighLow && props.account?.user?.high_low_plan) {
          try {
            // Reset high/low plan using the macros we just saved
            await resetHighLowPlan(carbsGrams, fatGrams);
            // Reload account again to get the updated high/low plan
            if (props.loadPortalAccount) {
              await props.loadPortalAccount();
            }
            // Show the modal
            updateModalState({ showHighLowResetModal: true });
          } catch (err) {
            console.error("Error resetting high/low plan:", err);
            // Still reload and show success message even if reset fails
            if (props.loadPortalAccount) {
              await props.loadPortalAccount();
            }
          }
        }
        
        props.openSnackbar("Calories & macros have been updated", false);
      })
      .catch((err) => {
        updateUiState({ isLoading: false });
        props.openSnackbar("Unable to update calories & macros", true);
      });
  };

  const handleCalorieGoalChange = (value) => {
    if (caloriesGoalLocked) {
      return;
    }
    const newCalorieGoal = parseInt(value) || 0;

    const oldCalorieGoal = calorieGoal;
    // Always allow the calorie goal to change
    updateMacroState({ calorieGoal: newCalorieGoal });

    // Skip calculation if new calorie goal is 0 or less
    if (newCalorieGoal <= 0) {
      // Just validate current macros with new calorie goal
      const macroErrors = validateMacros({
        ...getCurrentState(),
        calorieGoal: newCalorieGoal
      });
      const floorErrors = validateCalorieFloor(getCurrentState());
      const highLowErrors = validateHighLowPlan(
        { ...getCurrentState(), calorieGoal: newCalorieGoal },
        props.account?.user
      );

      const allErrors = [];
      if (macroErrors) allErrors.push(...macroErrors);
      if (floorErrors) allErrors.push(...floorErrors);
      if (highLowErrors) allErrors.push(...highLowErrors);

      updateDataState({ calcError: allErrors.length > 0 ? allErrors : null });
      return;
    }

    // Only recalculate macros if the old calorie goal is valid (not 0)
    if (oldCalorieGoal > 0) {
      const newMacros = recalculateMacrosForCalorieGoalChange({
        ...getCurrentState(),
        calorieGoal: oldCalorieGoal,
        newCalorieGoal
      });

      const errors = validateMacros({
        ...getCurrentState(),
        protein: newMacros.protein,
        carbs: newMacros.carbs,
        fat: newMacros.fat,
        calorieGoal: newCalorieGoal
      });

      if (errors) {
        // Show errors but keep the new calorie goal value (don't revert)
        // Validate current macros with new calorie goal to show relevant errors
        const macroErrors = validateMacros({
          ...getCurrentState(),
          calorieGoal: newCalorieGoal
        });
        const floorErrors = validateCalorieFloor(getCurrentState());
        const highLowErrors = validateHighLowPlan(
          { ...getCurrentState(), calorieGoal: newCalorieGoal },
          props.account?.user
        );

        const allErrors = [];
        if (macroErrors) allErrors.push(...macroErrors);
        if (floorErrors) allErrors.push(...floorErrors);
        if (highLowErrors) allErrors.push(...highLowErrors);

        updateDataState({ calcError: allErrors.length > 0 ? allErrors : null });
        return;
      }

      // Only update macros if validation passes
      updateMacros(newMacros.protein, newMacros.carbs, newMacros.fat);
      updateDataState({ calcError: [] });
    } else {
      // If old calorie goal is 0, we can't recalculate, so set macros to 0
      updateMacros(0, 0, 0);
      validateAndSetErrors(0, 0, 0, newCalorieGoal);
    }
  };

  const handleCalorieFloorChange = (event) => {
    if (calorieFloorLocked) {
      return;
    }
    const newCalorieFloor = parseInt(event.target.value) || 0;
    updateMacroState({ calorieFloor: newCalorieFloor });

    // Validate calorie floor and also check if calorie goal is below it
    const floorErrors = validateCalorieFloor({
      ...getCurrentState(),
      calorieFloor: newCalorieFloor
    });
    const goalErrors =
      calorieGoal < newCalorieFloor
        ? ["Error: Calorie goal cannot be below calorie floor."]
        : null;

    const allErrors = [];
    if (floorErrors) allErrors.push(...floorErrors);
    if (goalErrors) allErrors.push(...goalErrors);

    // Also validate current macros with new floor
    const macroErrors = validateMacros({
      ...getCurrentState(),
      calorieFloor: newCalorieFloor
    });
    if (macroErrors) {
      // Filter out the old calorie goal error since we're handling it separately above
      const filteredMacroErrors = macroErrors.filter(
        (err) => !err.includes("cannot be below calorie floor")
      );
      allErrors.push(...filteredMacroErrors);
    }

    if (allErrors.length > 0) {
      updateDataState({ calcError: allErrors });
    } else {
      updateDataState({ calcError: [] });
    }
  };

  const toggleLock = () => {
    // Don't allow unlocking when in percent view
    if (viewMode === "percent") {
      return;
    }
    
    // If trying to unlock, check check-in status
    if (caloriesGoalLocked) {
      const vacationMode = props.account?.user?.vacation_mode || 0;
      const lastWeighInDate = props.account?.user?.lastWeighInDate;
      const daysAfterLastCheckIn = lastWeighInDate ? getDaysSinceWeighIn(lastWeighInDate) : 0;
      
      if (vacationMode !== 0) {
        updateModalState({ showVacationModal: true });
        return;
      } else if (daysAfterLastCheckIn >= 1 && daysAfterLastCheckIn < 5) {
        updateModalState({ showCheckinFormPortal: true });
        return;
      } else if (daysAfterLastCheckIn >= 5) {
        updateModalState({ showUserCheckinWarning: true });
        return;
      } else if (daysAfterLastCheckIn >= 1) {
        // Only allow unlock if daysAfterLastCheckIn < 1
        return;
      }
    } else {
      // If trying to lock, check if we're already at the 2-item limit
      const lockedCount = getLockedCount();
      if (lockedCount >= 2) {
        props.openSnackbar("Only 2 items can be locked at the same time.", true);
        return;
      }
    }
    
    updateMacroState({ caloriesGoalLocked: !caloriesGoalLocked });
  };

  const toggleCalorieFloorLock = () => {
    updateMacroState({ calorieFloorLocked: !calorieFloorLocked });
  };

  const clearPercentageInputs = () => {
    updatePercentageInputs({
      proteinPercentageInput: null,
      carbsPercentageInput: null,
      fatPercentageInput: null,
    });
    setEditingPercentageMacro(null);
  };

  const setMacroInputDraft = (macro, value) => {
    setMacroInputDrafts((prev) => ({ ...prev, [macro]: value }));
  };

  const clearMacroInputDrafts = () => {
    setMacroInputDrafts({
      protein: null,
      carbs: null,
      fat: null,
    });
  };

  const getMacroInputValue = (macro, grams) => {
    const draftValue = macroInputDrafts[macro];
    if (draftValue === null || draftValue === undefined) {
      return Math.round(grams);
    }
    return draftValue;
  };

  const updateMacros = (protein, carbs, fat, calorieGoal = null) => {
    updateMacroState({
      proteinGrams: protein,
      carbsGrams: carbs,
      fatGrams: fat,
      ...(calorieGoal !== null && { calorieGoal }),
    });
    clearMacroInputDrafts();
    clearPercentageInputs();
  };

  const validateAndSetErrors = (protein, carbs, fat, calories, excludeHighLowErrors = true) => {
    const macroErrors = validateMacros({ ...getCurrentState(), protein, carbs, fat, calorieGoal: calories });
    const floorErrors = validateCalorieFloor({ ...getCurrentState(), protein, carbs });
    const highLowErrors = validateHighLowPlan(
      { ...getCurrentState(), protein, carbs, fat, calorieGoal: calories },
      props.account?.user
    );

    const allErrors = [];
    if (macroErrors) allErrors.push(...macroErrors);
    if (floorErrors) allErrors.push(...floorErrors);
    // Never include high/low errors in calcError (they don't block save and we show modal instead)
    // if (!excludeHighLowErrors && highLowErrors) allErrors.push(...highLowErrors);

    if (allErrors.length > 0) {
      updateDataState({ calcError: allErrors });
      return { valid: false, highLowInvalid: !!highLowErrors };
    }
    updateDataState({
      calcError: [],
      lastValidMacros: { protein, carbs, fat },
    });
    return { valid: true, highLowInvalid: !!highLowErrors };
  };

  const setPercentageInput = (macro, value) => {
    const updates = {};
    if (macro === "protein") {
      updates.proteinPercentageInput = value === "" ? "" : value;
    } else if (macro === "carbs") {
      updates.carbsPercentageInput = value === "" ? "" : value;
    } else if (macro === "fat") {
      updates.fatPercentageInput = value === "" ? "" : value;
    }
    updatePercentageInputs(updates);
  };

  const handleRestoreCalorieGoal = () => {
    // Check if calorie goal is locked and the new goal differs from current
    if (caloriesGoalLocked && recommendedMacros && calorieGoal !== null) {
      const newCalorieGoal = recommendedMacros.calories;
      if (Math.round(calorieGoal) !== Math.round(newCalorieGoal)) {
        props.openSnackbar("Calorie goal needs to be unlocked first", true);
        return;
      }
    }
    handleCalorieGoalChange(recommendedMacros.calories);
  };

  const handleRestoreCalorieFloor = () => {
    if (recommendedMacros) {
      updateMacroState({ calorieFloor: recommendedMacros.calorie_floor });
    }
  };

  const handleRestoreMacros = () => {
    if (!recommendedMacros) return;

    const newCalorieGoal =
      recommendedMacros.protein * 4 +
      recommendedMacros.carbs * 4 +
      recommendedMacros.fat * 9;

    // Check if calorie goal is locked and the new goal differs from current
    if (caloriesGoalLocked && calorieGoal !== null) {
      if (Math.round(calorieGoal) !== Math.round(newCalorieGoal)) {
        props.openSnackbar("Calorie goal needs to be unlocked first", true);
        return;
      }
    }

    const errors = validateMacros({
      ...getCurrentState(),
      protein: recommendedMacros.protein,
      carbs: recommendedMacros.carbs,
      fat: recommendedMacros.fat,
      calorieGoal: newCalorieGoal
    });

    const validation = validateAndSetErrors(
      recommendedMacros.protein,
      recommendedMacros.carbs,
      recommendedMacros.fat,
      newCalorieGoal
    );
    if (!validation.valid) {
      return;
    }

    updateMacros(
      recommendedMacros.protein,
      recommendedMacros.carbs,
      recommendedMacros.fat,
      newCalorieGoal
    );
  };

  const applyMacroInputChange = (macro, grams) => {
    // Check if calorie goal is locked or any macro is locked
    const hasMacroLock = proteinLocked || carbsLocked || fatLocked;
    const hasLock = caloriesGoalLocked || hasMacroLock;

    // Special case: calorie goal unlocked but macros are locked
    // Just update the changed macro and recalculate calorie goal
    if (!caloriesGoalLocked && hasMacroLock) {
      const finalProtein =
        proteinLocked && macro !== "protein"
          ? proteinGrams
          : macro === "protein"
          ? grams
          : proteinGrams;
      const finalCarbs =
        carbsLocked && macro !== "carbs"
          ? carbsGrams
          : macro === "carbs"
          ? grams
          : carbsGrams;
      const finalFat =
        fatLocked && macro !== "fat"
          ? fatGrams
          : macro === "fat"
          ? grams
          : fatGrams;

      // Calculate new calorie goal from all macros
      const newCalorieGoal = finalProtein * 4 + finalCarbs * 4 + finalFat * 9;

      validateAndSetErrors(
        finalProtein,
        finalCarbs,
        finalFat,
        newCalorieGoal
      );

      updateMacros(finalProtein, finalCarbs, finalFat, newCalorieGoal);
      return;
    }

    if (hasLock) {
      // Use recalculation rules as primary method
      const newMacros = recalculateMacrosForMacroChange({
        ...getCurrentState(),
        macro,
        newGrams: grams
      });

      // Preserve the exact input value for the macro being changed
      const finalProtein = macro === "protein" ? grams : newMacros.protein;
      const finalCarbs = macro === "carbs" ? grams : newMacros.carbs;
      const finalFat = macro === "fat" ? grams : newMacros.fat;

      // Recalculate calorie goal with final values
      const finalCalorieGoal = finalProtein * 4 + finalCarbs * 4 + finalFat * 9;

      validateAndSetErrors(
        finalProtein,
        finalCarbs,
        finalFat,
        caloriesGoalLocked ? calorieGoal : finalCalorieGoal
      );
      // Update all macros and calorie goal (only update calorie goal if not locked)
      if (caloriesGoalLocked) {
        updateMacros(finalProtein, finalCarbs, finalFat);
      } else {
        updateMacros(finalProtein, finalCarbs, finalFat, finalCalorieGoal);
      }
    } else {
      // Nothing is locked - just recalculate calorie goal
      const finalProtein = macro === "protein" ? grams : proteinGrams;
      const finalCarbs = macro === "carbs" ? grams : carbsGrams;
      const finalFat = macro === "fat" ? grams : fatGrams;
      const newCalorieGoal = finalProtein * 4 + finalCarbs * 4 + finalFat * 9;

      const validation = validateAndSetErrors(
        finalProtein,
        finalCarbs,
        finalFat,
        newCalorieGoal
      );
      if (!validation.valid) {
        // Skip calculation but input value is already updated
        return;
      }

      updateMacros(finalProtein, finalCarbs, finalFat, newCalorieGoal);
      updateDataState({ calcError: [] });
    }
  };

  const handleMacroInputChange = (macro, value) => {
    const digitsOnly = String(value || "").replace(/\D/g, "");
    const limitedDigits = digitsOnly.slice(0, 4);
    const grams = limitedDigits === "" ? 0 : parseInt(limitedDigits, 10) || 0;

    setMacroInputDraft(macro, limitedDigits);
    debouncedApplyMacroInputChange(() => {
      applyMacroInputChange(macro, grams);
      setMacroInputDraft(macro, null);
    });
  };

  const handlePercentageChange = (macro, percentage) => {
    setEditingPercentageMacro(macro);
    // Convert to string and trim whitespace
    const cleanPercentage = String(percentage).trim();

    // Store the raw input value (can be empty string)
    setPercentageInput(macro, cleanPercentage);

    // Treat empty string as 0 for calculation
    let percentageValue;
    if (cleanPercentage === "") {
      percentageValue = 0;
    } else {
      const parsedValue = parseFloat(cleanPercentage);
      if (isNaN(parsedValue)) {
        return;
      }
      percentageValue = parsedValue;
    }
    // Clamp percentage to valid range for calculation
    const clampedPercentage = Math.max(0, Math.min(100, percentageValue));
    debouncedApplyPercentageInputChange(() => {

    // Guard against division by zero - validation will catch the error
    if (calorieGoal <= 0) {
      const errors = validateMacros(getCurrentState());
      const floorErrors = validateCalorieFloor(getCurrentState());
      const highLowErrors = validateHighLowPlan(getCurrentState(), props.account?.user);
      const allErrors = [];
      if (errors) allErrors.push(...errors);
      if (floorErrors) allErrors.push(...floorErrors);
      if (highLowErrors) allErrors.push(...highLowErrors);
      updateDataState({ calcError: allErrors.length > 0 ? allErrors : null });
      return;
    }

    const currentProteinPercentage = ((proteinGrams * 4) / calorieGoal) * 100;
    const currentCarbsPercentage = ((carbsGrams * 4) / calorieGoal) * 100;
    const currentFatPercentage = ((fatGrams * 9) / calorieGoal) * 100;

    let newProteinGrams = proteinGrams;
    let newCarbsGrams = carbsGrams;
    let newFatGrams = fatGrams;

    // Calculate new grams for the changed macro from percentage
    let newGrams;
    switch (macro) {
      case "protein":
        newGrams = ((clampedPercentage / 100) * calorieGoal) / 4;
        break;
      case "carbs":
        newGrams = ((clampedPercentage / 100) * calorieGoal) / 4;
        break;
      case "fat":
        newGrams = ((clampedPercentage / 100) * calorieGoal) / 9;
        break;
      default:
        return;
    }

    // Check if calorie goal is locked or any macro is locked
    const hasMacroLock = proteinLocked || carbsLocked || fatLocked;
    const hasLock = caloriesGoalLocked || hasMacroLock;

    // Special case: calorie goal unlocked but macros are locked
    // Just update the changed macro and recalculate calorie goal
    if (!caloriesGoalLocked && hasMacroLock) {
      newProteinGrams =
        proteinLocked && macro !== "protein"
          ? proteinGrams
          : macro === "protein"
          ? newGrams
          : proteinGrams;
      newCarbsGrams =
        carbsLocked && macro !== "carbs"
          ? carbsGrams
          : macro === "carbs"
          ? newGrams
          : carbsGrams;
      newFatGrams =
        fatLocked && macro !== "fat"
          ? fatGrams
          : macro === "fat"
          ? newGrams
          : fatGrams;
    } else if (hasLock) {
      // Use new recalculation rules as primary method
      const recalculated = recalculateMacrosForMacroChange({
        ...getCurrentState(),
        macro,
        newGrams
      });

      newProteinGrams = macro === "protein" ? newGrams : recalculated.protein;
      newCarbsGrams = macro === "carbs" ? newGrams : recalculated.carbs;
      newFatGrams = macro === "fat" ? newGrams : recalculated.fat;
    } else {
      // Nothing is locked - use old behavior: distribute remaining percentage proportionally
      const remainingPercentage = 100 - clampedPercentage;
      switch (macro) {
        case "protein": {
          newProteinGrams = newGrams;
          const carbsFatTotal = currentCarbsPercentage + currentFatPercentage;
          if (carbsFatTotal === 0) {
            newCarbsGrams = ((remainingPercentage / 100) * calorieGoal) / 4 / 2;
            newFatGrams = ((remainingPercentage / 100) * calorieGoal) / 9 / 2;
          } else {
            const carbsRatio = currentCarbsPercentage / carbsFatTotal;
            const fatRatio = currentFatPercentage / carbsFatTotal;
            newCarbsGrams =
              (((remainingPercentage * carbsRatio) / 100) * calorieGoal) / 4;
            newFatGrams =
              (((remainingPercentage * fatRatio) / 100) * calorieGoal) / 9;
          }
          break;
        }
        case "carbs": {
          newCarbsGrams = newGrams;
          const proteinFatTotal =
            currentProteinPercentage + currentFatPercentage;
          if (proteinFatTotal === 0) {
            newProteinGrams =
              ((remainingPercentage / 100) * calorieGoal) / 4 / 2;
            newFatGrams = ((remainingPercentage / 100) * calorieGoal) / 9 / 2;
          } else {
            const proteinRatio = currentProteinPercentage / proteinFatTotal;
            const fatRatioForCarbs = currentFatPercentage / proteinFatTotal;
            newProteinGrams =
              (((remainingPercentage * proteinRatio) / 100) * calorieGoal) / 4;
            newFatGrams =
              (((remainingPercentage * fatRatioForCarbs) / 100) * calorieGoal) /
              9;
          }
          break;
        }
        case "fat": {
          newFatGrams = newGrams;
          const proteinCarbsTotal =
            currentProteinPercentage + currentCarbsPercentage;
          if (proteinCarbsTotal === 0) {
            newProteinGrams =
              ((remainingPercentage / 100) * calorieGoal) / 4 / 2;
            newCarbsGrams = ((remainingPercentage / 100) * calorieGoal) / 4 / 2;
          } else {
            const proteinRatioForFat =
              currentProteinPercentage / proteinCarbsTotal;
            const carbsRatioForFat = currentCarbsPercentage / proteinCarbsTotal;
            newProteinGrams =
              (((remainingPercentage * proteinRatioForFat) / 100) *
                calorieGoal) /
              4;
            newCarbsGrams =
              (((remainingPercentage * carbsRatioForFat) / 100) * calorieGoal) /
              4;
          }
          break;
        }
      }
    }

    // Always update the macros, even if there are validation errors
    updateMacroState({
      proteinGrams: newProteinGrams,
      carbsGrams: newCarbsGrams,
      fatGrams: newFatGrams,
    });

    // Update calorie goal based on lock state
    if (caloriesGoalLocked) {
      // Calorie goal is locked, don't change it
      // finalCalories stays as calorieGoal
    } else if (hasLock) {
      // Some macro is locked but calorie goal is not - recalculate calorie goal
      const newCalorieGoal =
        newProteinGrams * 4 + newCarbsGrams * 4 + newFatGrams * 9;
      updateMacroState({ calorieGoal: newCalorieGoal });
    } else {
      // Nothing is locked - calorie goal stays the same (just redistributing percentages)
      // finalCalories stays as calorieGoal
    }

    // Validate and show errors, but don't prevent the update
    const finalCalories =
      caloriesGoalLocked || !hasLock
        ? calorieGoal
        : newProteinGrams * 4 + newCarbsGrams * 4 + newFatGrams * 9;
    const macroErrors = validateMacros({
      ...getCurrentState(),
      protein: newProteinGrams,
      carbs: newCarbsGrams,
      fat: newFatGrams,
      calorieGoal: finalCalories
    });
    const floorErrors = validateCalorieFloor({
      ...getCurrentState(),
      protein: newProteinGrams,
      carbs: newCarbsGrams
    });
    const highLowErrors = validateHighLowPlan(
      {
        ...getCurrentState(),
        protein: newProteinGrams,
        carbs: newCarbsGrams,
        fat: newFatGrams,
        calorieGoal: finalCalories
      },
      props.account?.user
    );

    const allErrors = [];
    if (macroErrors) allErrors.push(...macroErrors);
    if (floorErrors) allErrors.push(...floorErrors);
    if (highLowErrors) allErrors.push(...highLowErrors);

    if (allErrors.length > 0) {
      updateDataState({ calcError: allErrors });
    } else {
      const nextProteinTarget =
        macro === "protein"
          ? newProteinGrams
          : isValidValue(lastValidMacros?.protein)
            ? lastValidMacros.protein
            : newProteinGrams;

      updateDataState({
        calcError: [],
        lastValidMacros: {
          protein: nextProteinTarget,
          carbs: newCarbsGrams,
          fat: newFatGrams,
        },
      });
    }
    });
  };

  const getLockedCount = () => {
    let count = 0;
    if (caloriesGoalLocked) count++;
    if (proteinLocked) count++;
    if (carbsLocked) count++;
    if (fatLocked) count++;
    return count;
  };

  const handleMacroLockToggle = (macro) => {
    const currentLocked = macro === "protein" ? proteinLocked : macro === "carbs" ? carbsLocked : fatLocked;
    
    // If trying to lock
    if (!currentLocked) {
      const lockedCount = getLockedCount();
      if (lockedCount >= 2) {
        props.openSnackbar("Only 2 items can be locked at the same time.", true);
        return;
      }
    }
    
    // Toggle the lock
    const updates = {};
    if (macro === "protein") {
      updates.proteinLocked = !proteinLocked;
    } else if (macro === "carbs") {
      updates.carbsLocked = !carbsLocked;
    } else if (macro === "fat") {
      updates.fatLocked = !fatLocked;
    }
    updateMacroState(updates);
  };

  const macroItems = [
    {
      macro: "protein",
      grams: proteinGrams,
      gramsInputValue: getMacroInputValue("protein", proteinGrams),
      percentage:
        editingPercentageMacro === "protein" &&
        proteinPercentageInput !== null
          ? proteinPercentageInput
          : percentages.protein,
      locked: proteinLocked,
      onLockToggle: () => handleMacroLockToggle("protein"),
      colors: { strokeColor: "#ed8b2e", label: "PROTEIN" },
    },
    {
      macro: "carbs",
      grams: carbsGrams,
      gramsInputValue: getMacroInputValue("carbs", carbsGrams),
      percentage:
        editingPercentageMacro === "carbs" && carbsPercentageInput !== null
          ? carbsPercentageInput
          : percentages.carbs,
      locked: carbsLocked,
      onLockToggle: () => handleMacroLockToggle("carbs"),
      colors: { strokeColor: "#5c2ff0", label: "CARBS" },
    },
    {
      macro: "fat",
      grams: fatGrams,
      gramsInputValue: getMacroInputValue("fat", fatGrams),
      percentage:
        editingPercentageMacro === "fat" && fatPercentageInput !== null
          ? fatPercentageInput
          : percentages.fat,
      locked: fatLocked,
      onLockToggle: () => handleMacroLockToggle("fat"),
      colors: { strokeColor: "#e328a2", label: "FAT" },
    },
  ];

  return (
    <Accordion
      show={show}
      setShow={(value) => updateUiState({ show: value })}
      icon={SettingIcon}
      settingName="Edit calories and macros"
      currentSetting="Configure"
      alternateColor
      roundBottom
    >
      <Container>
        <Section>
          <TitleAndInput>
            <TitleText>Calorie Goal</TitleText>
            <CalorieGoalContainer
              style={{ opacity: viewMode === "percent" ? 0.5 : 1 }}
            >
              <Input
                type="number"
                step="1"
                value={Math.round(calorieGoal)}
                onChange={(e) => handleCalorieGoalChange(e.target.value)}
                disabled={caloriesGoalLocked}
              />
              <IconButton
                key="lock"
                aria-label="lock"
                color="inherit"
                onClick={toggleLock}
                size="medium"
              >
                {caloriesGoalLocked ? (
                  <LockIcon style={{ width: 24, height: 24 }} />
                ) : (
                  <LockOpenIcon style={{ width: 24, height: 24 }} />
                )}
              </IconButton>
            </CalorieGoalContainer>
          </TitleAndInput>
          {props.account.user.custom_calories !== null &&
            calorieGoal !== props.account.user.custom_calories && (
              <OriginalGoalText>
                Original Calorie Goal:{" "}
                {Math.round(props.account.user.custom_calories)}
              </OriginalGoalText>
            )}
          {recommendedMacros &&
            Math.round(calorieGoal) !==
              Math.round(recommendedMacros.calories) && (
              <RestoreButton onClick={handleRestoreCalorieGoal}>
                Restore Suggested: {Math.round(recommendedMacros.calories)}
                <RefreshIcon style={{ fontSize: 16 }} />
              </RestoreButton>
            )}
        </Section>

        <Section>
          <TitleAndInput>
            <TitleText>
              Calorie Floor
              <HelpTip message="The calorie floor is the lowest we will drop your daily calories through our adjustments at check-ins." />
            </TitleText>
            <CalorieGoalContainer>
              <Input
                type="number"
                step="1"
                value={Math.round(calorieFloor)}
                onChange={handleCalorieFloorChange}
                disabled={calorieFloorLocked}
              />
              <IconButton
                key="lock-floor"
                aria-label="lock-floor"
                color="inherit"
                onClick={toggleCalorieFloorLock}
                size="medium"
              >
                {calorieFloorLocked ? (
                  <LockIcon style={{ width: 24, height: 24 }} />
                ) : (
                  <LockOpenIcon style={{ width: 24, height: 24 }} />
                )}
              </IconButton>
            </CalorieGoalContainer>
          </TitleAndInput>
          {props.account.user.custom_calorie_floor !== null &&
            Math.round(calorieFloor) !==
              Math.round(props.account.user.custom_calorie_floor) && (
              <OriginalGoalText>
                Original Calorie Floor:{" "}
                {Math.round(props.account.user.custom_calorie_floor)}
              </OriginalGoalText>
            )}
          {recommendedMacros &&
            Math.round(calorieFloor) !==
              Math.round(recommendedMacros.calorie_floor) && (
              <RestoreButton onClick={handleRestoreCalorieFloor}>
                Restore Suggested: {Math.round(recommendedMacros.calorie_floor)}
                <RefreshIcon style={{ fontSize: 16 }} />
              </RestoreButton>
            )}
        </Section>
        <Section>
          <ViewToggle>
            <ToggleButton
              active={viewMode === "grams"}
              onClick={() => updateUiState({ viewMode: "grams" })}
            >
              Grams
            </ToggleButton>
            <ToggleButton
              active={viewMode === "percent"}
              onClick={() => updateUiState({ viewMode: "percent" })}
            >
              Percent
            </ToggleButton>
          </ViewToggle>

          <MacroGrid>
            {macroItems.map((item) => (
              <MacroItemComponent
                key={item.macro}
                macro={item.macro}
                grams={item.grams}
                gramsInputValue={item.gramsInputValue}
                percentage={item.percentage}
                viewMode={viewMode}
                locked={item.locked}
                onLockToggle={item.onLockToggle}
                onGramsChange={handleMacroInputChange}
                onPercentageChange={handlePercentageChange}
                colors={item.colors}
              />
            ))}
          </MacroGrid>

          {recommendedMacros &&
            (Math.round(proteinGrams) !==
              Math.round(recommendedMacros.protein) ||
              Math.round(carbsGrams) !== Math.round(recommendedMacros.carbs) ||
              Math.round(fatGrams) !== Math.round(recommendedMacros.fat)) && (
              <RestoreButton
                onClick={handleRestoreMacros}
                style={{ margin: "0 auto" }}
              >
                Restore Suggested: {Math.round(recommendedMacros.protein)}P{" "}
                {Math.round(recommendedMacros.carbs)}C{" "}
                {Math.round(recommendedMacros.fat)}F
                <RefreshIcon style={{ fontSize: 16 }} />
              </RestoreButton>
            )}

          {calcError && calcError.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginTop: "16px",
                marginBottom: "16px",
              }}
            >
              {calcError.map((error, index) => (
                <ErrorBox key={index}>
                  <ErrorIcon>!</ErrorIcon>
                  <span>{error}</span>
                </ErrorBox>
              ))}
            </div>
          )}
        </Section>
      </Container>
      <ButtonRow>
        <SaveButton
          disabled={isLoading || calcError.length > 0}
          isLoading={isLoading}
          onClick={handleSave}
        />
      </ButtonRow>
      
      <VacationModeModal
        setting="calories"
        open={showVacationModal}
        onClose={() => updateModalState({ showVacationModal: false })}
        isPaused={
          props.account?.user?.vacation_mode > 0 &&
          !props.account?.user?.vacation_mode_info?.practice_mode
        }
      />

      <UserCheckinWarning
        open={showUserCheckinWarning}
        onClose={() => updateModalState({ showUserCheckinWarning: false })}
        daysAfterLastCheckIn={
          props.account?.user?.lastWeighInDate
            ? getDaysSinceWeighIn(props.account.user.lastWeighInDate)
            : 0
        }
      />

      <CheckinFormPortal
        open={showCheckinFormPortal}
        onClose={() => updateModalState({ showCheckinFormPortal: false })}
        openFeedback={props.openSnackbar}
        userId={props.userId}
        portalId={props.portalId}
        loadPortalAccount={props.loadPortalAccount}
        weightString={props.weightString}
        previousWeight={props.previousWeight}
        previousBodyfat={props.account?.user?.body_fat}
        useMetricUnits={props.account?.user?.use_metric_units}
        daysAfterLastCheckIn={
          props.account?.user?.lastWeighInDate
            ? getDaysSinceWeighIn(props.account.user.lastWeighInDate)
            : 0
        }
        gender={props.account?.user?.gender}
        checkInDate={props.account?.user?.check_in_date}
      />

      <Dialog open={showHighLowResetModal} onClose={() => updateModalState({ showHighLowResetModal: false })}>
        <DialogContent className='avn-modal-content-container'>
          <img src={HighLowIcon} alt="high-low-days-icon" />
          <h4>Set up High / Low Days</h4>
          <p style={{maxWidth: '510px'}}>Your high/low plan has been reset due to changes in your macros. Would you like to set it up again?</p>
          <DialogActions className='avn-modal-actions-container'>
            <button className='avn-modal-action' onClick={() => updateModalState({ showHighLowResetModal: false })}>Ok got it</button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </Accordion>
  );
};

export default UpdateCaloriesMacros;
